<template>
  <b-overlay :show="loading" opacity="0.8">
    <send-prescriptions-modal
      :consultation-data="consultationData"
      :prescription-data="prescriptionData"
      :is-add-active.sync="togglePrescriptionModal"
      :patient-data="patientData"
      resource="prescription_deliveries"
      @refetchData="fetchAllData"
    />

    <b-card class="rounded-lg">
      <template #header>
        <div class="d-flex justify-content-between align-items-center w-100 pb-1" style="border-bottom: 2px solid rgb(238, 238, 238);">
          <h4 class="text-dark m-0">
            Prescriptions {{ records.length > 0 ? `(${records.length})` : '' }}
          </h4>
          
          <div class="d-flex align-items-center justify-content-start">
            <b-dropdown v-if="can('prescription_deliveries:CanAddNew')" variant="link" class="p-0" no-caret :right="$store.state.appConfig.isRTL" dropleft>
              <template #button-content>
                <feather-icon icon="MoreVerticalIcon" size="20" class="align-middle text-body" />
              </template>

              <b-dropdown-item @click="onSend">
                <feather-icon icon="SendIcon" />
                <span class="align-middle ml-50">Send to partner facility</span>
              </b-dropdown-item>
            </b-dropdown>

            <div v-if="can('prescriptions:CanAddNew') && currentUserProfession === 'doctor'" class="d-flex align-items-center">
              <b-button size="sm" variant="link" class="p-0" @click="onAddNew">
                <feather-icon
                  icon="PlusCircleIcon"
                  size="25"
                />
              </b-button>
            </div>
          </div>
        </div>
      </template>
      
      
      <div class="" style="height: 480px; overflow-y: scroll;">
        <b-overlay :show="loading" opacity="0.5">
          <add-or-update-prescriptions-modal
            :editing-data.sync="editingData"
            :is-add-active.sync="toggleModal"
            :consultation-data="consultationData"
            resource="prescriptions"
            modal-title="Prescriptions"
            @create="onCreate"
            @refetchData="onRefresh"
          />
  
          <div>
            <list-table
              :records="records" 
              :columns="tableColumns"
              :current-page-number="1"
              :show-import-button="false"
              :show-export-button="false"
              :show-search="false"
              :total-records="totalRecords"
            >

            <template #cell(regiment)="data">
              <p>
                <b-badge
                  variant="info"
                  class="text-capitalize"
                >
                  Dose
                </b-badge>
                <span> {{ data.item.dose }} </span> 
              </p>
              <p>
                <b-badge
                  variant="info"
                  class="text-capitalize"
                >
                  When
                </b-badge>
                <span> {{ getValueFromSource(data.item, 'drug.dose_frequency.name') }} </span> 
              </p>
              <p>
                <b-badge
                  :variant="`info`"
                  class="text-capitalize"
                >
                  For
                </b-badge>
                <span> {{ `${data.item.duration} ${startCase(data.item.duration_unit)}` }} </span> 
              </p>
              <p>
                <b-badge
                  :variant="`info`"
                  class="text-capitalize"
                >
                  How
                </b-badge>
                <span> {{ getValueFromSource(data.item, 'drug.route.name') || "-" }} </span> 
              </p>
            </template>

            <template #cell(status)="data">
              <b-badge
                v-if="getValueFromSource(data.item, 'prescription_delivery') && getValueFromSource(data.item, 'prescription_delivery.status')"
                pill
                :variant="resolveStatusVariant(getValueFromSource(data.item, 'prescription_delivery.status'))"
                class="text-capitalize"
              >
                {{ getUserStatusText(getValueFromSource(data.item, 'prescription_delivery.status')) }}
              </b-badge>
              <span v-else>-</span>
            </template>
  
            <template #cell(actions)="data">
              <div v-if="currentUserProfession === 'doctor'">
                <b-dropdown
                  variant="link"
                  no-caret
                  :right="$store.state.appConfig.isRTL"
                >
                  <template #button-content>
                    <feather-icon
                      icon="MoreVerticalIcon"
                      size="16"
                      class="align-middle text-body"
                    />
                  </template>
  
                  <b-dropdown-item @click="onEdit(data.item)">
                    <feather-icon icon="EditIcon" />
                    <span class="align-middle ml-50">Edit</span>
                  </b-dropdown-item>
  
                  <b-dropdown-item @click="onDelete(data.item)">
                    <feather-icon icon="Trash2Icon" />
                    <span class="align-middle ml-50">Delete</span>
                  </b-dropdown-item>
                </b-dropdown>
              </div>
              <span v-else class="ml-2">-</span>
            </template>
            </list-table>
          </div>
        </b-overlay>
      </div>
    </b-card>
  </b-overlay>
</template>

<script>
import {
  BCard,
  BImg,
  BRow,
  BCol,
  BBadge,
  BAlert,
  BButton,
  BAvatar,
  BOverlay,
  BDropdown,
  BDropdownItem,
  BDropdownGroup
} from 'bootstrap-vue'

import { get, startCase } from 'lodash';

import Ripple from 'vue-ripple-directive'
import ListTable from "@/@core/components/ListTable/ListTable.vue";
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import SendPrescriptionsModal from '@/@core/components/shared/admin/modals/SendPrescriptionsModal.vue';
import AddOrUpdatePrescriptionsModal from '@/@core/components/shared/admin/modals/AddOrUpdatePrescriptionsModal.vue';

export default {
  components: {
    BImg,
    BRow,
    BCol,
    BCard,
    BBadge,
    BAlert,
    BAvatar,
    BButton,
    BOverlay,
    BDropdown,
    BDropdownItem,
    BDropdownGroup,

    ListTable,
    SendPrescriptionsModal,
    AddOrUpdatePrescriptionsModal
  },
  directives: {
    Ripple,
  },
  props: {
    consultationData: {
      type: Object,
      required: true,
    },
    patientData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,

      editingData: null,
      prescriptionData: null,
      toggleModal: false,
      togglePrescriptionModal: false,
      
      records: [],
      tableColumns: [
        { key: 'drug.generic_name', label: 'Medication', sortable: false, },
        { key: 'regiment', label: 'Regiment', sortable: false, },
        { key: 'prescription_delivery.pharmacy.pharmacy_name', label: 'Pharmacy', sortable: false, formatter: (val) => val || '-' },
        { key: 'status', label: 'Status', sortable: false, formatter: (val) => val || '-' },
        { key: 'actions' },
      ],
    }
  },
  computed: {
    totalRecords: {
      get(){
        return this.$store.getters[`navigation/totalRecords`];
      },
      set(value){
        this.$store.commit(`navigation/UPDATE_TOTAL_RECORDS`, value)
      }
    },
  },
  created() {
    this.fetchAllData();
  },
  methods: {
    async fetchAllData() {
      try {
        this.loading = true; 

        const query = {
          page: 1,
          limit: 100,
          consultation_id: this.consultationData._id,
        }

        const request = await this.useJwt().mainService.prescriptions.list(query);
        const { data, pagination } = request.data;
        this.records = data;
        this.totalRecords = pagination.totalRecords
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        console.log("error_message", error_message)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    async onCreate(form_data){
      try {
        this.loading = true;

        const { payload, save_and_new } = form_data;
        await this.useJwt().mainService.prescriptions.create({
          ...payload,
          consultation_id: this.consultationData._id
        });
        this.fetchAllData();

        if (save_and_new){
          this.toggleModal = false;
          setTimeout(() => {
            this.toggleModal = true;
          }, 100);
        } else {
          this.toggleModal = false;
        }

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            variant: 'success',
            icon: 'CheckCircleIcon',
            text: `Record added successfully`
          },
        });
      } catch (error) {
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false
      }
    },
    async onDelete(form_data){
      try {
        this.loading = true;

        await this.useJwt().mainService.prescriptions.delete(form_data.id);
        this.$emit('refresh')
        this.fetchAllData();

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Success!',
            icon: 'CheckCircleIcon',
            variant: 'success',
            text: `Record deleted successfully`
          },
        });
      } catch (error){
        const error_message = get(error, "response.data.message") || error.message
        this.errorMessage = error_message;

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Error!',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
            text: error_message
          },
        });
      } finally {
        this.loading = false;
      }
    },
    onSend() {
      this.prescriptionData = this.records.filter(record => {
        return record.prescription_delivery?.status === "out_of_stock" || !record.prescription_delivery
      });
      this.togglePrescriptionModal = true;
    },
    onEdit(record){
      this.editingData = record;
      this.toggleModal = true;
    },
    onRefresh() {
      this.$emit('refresh')
      this.fetchAllData();
    },
    onAddNew(){
      this.editingData = null;
      this.toggleModal = true;
    },
    startCase
  }
}
</script>

<style scoped>
.profile-highlight {
  font-size: 18px !important;
}
.profile-highlight-title {
  font-size: 16px !important;
  color: lightgray;
  font-weight: normal;
}

.patient-name {
  font-size: 25px;
}
</style>

<style>
.w-10 {
  width: 13% !important;
}
</style>
